import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { enqueue, isExcludedPath, isTrackingEnabled, start } from './behaviorTracker';
import { installAutocapture } from './autocapture';

/**
 * 行動分析のルート監視。<Router> 直下に 1 つだけ置く。
 *
 * - pathname が変わるたびに、直前ページの page_leave（滞在 ms・可視 ms）と
 *   新ページの page_view（title・遷移元・クエリのキー名）を記録する
 * - pagehide（タブを閉じる/リロード）で最後のページの page_leave を記録する
 * - 定期送信（start）とクリック/エラーの自動収集（installAutocapture）を開始する
 *
 * /admin 等の除外パスでは記録しないが、滞在時間の起点は更新する。
 */
const BehaviorTrackerRoot: React.FC = () => {
  const location = useLocation();
  const previousPathRef = useRef<string | null>(null);
  const enteredAtRef = useRef<number>(Date.now());
  const hiddenSinceRef = useRef<number | null>(null);
  const hiddenTotalRef = useRef<number>(0);

  const recordLeave = (path: string): void => {
    if (isExcludedPath(path)) return;
    const now = Date.now();
    const hidden = hiddenTotalRef.current + (hiddenSinceRef.current !== null ? now - hiddenSinceRef.current : 0);
    const duration = Math.max(0, now - enteredAtRef.current);
    enqueue({
      event_type: 'page_leave',
      event_name: 'page_leave',
      page_path: path,
      duration_ms: duration,
      props: { visible_ms: Math.max(0, duration - hidden) },
    });
  };

  const recordLeaveRef = useRef(recordLeave);
  recordLeaveRef.current = recordLeave;

  useEffect(() => {
    if (!isTrackingEnabled()) return undefined;

    // tracker の pagehide（beacon 送信）より先に page_leave を積むため、先に登録する
    const onPageHide = (): void => {
      if (previousPathRef.current !== null) {
        recordLeaveRef.current(previousPathRef.current);
        enteredAtRef.current = Date.now();
        hiddenTotalRef.current = 0;
      }
    };
    const onVisibilityChange = (): void => {
      if (document.visibilityState === 'hidden') {
        hiddenSinceRef.current = Date.now();
      } else if (hiddenSinceRef.current !== null) {
        hiddenTotalRef.current += Date.now() - hiddenSinceRef.current;
        hiddenSinceRef.current = null;
      }
    };
    window.addEventListener('pagehide', onPageHide);
    document.addEventListener('visibilitychange', onVisibilityChange);

    const stop = start();
    const uninstall = installAutocapture(enqueue);

    return () => {
      window.removeEventListener('pagehide', onPageHide);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      uninstall();
      stop();
    };
  }, []);

  useEffect(() => {
    if (!isTrackingEnabled()) return;

    const path = location.pathname;
    const previous = previousPathRef.current;
    if (previous === path) return; // StrictMode の二重実行や同一パスへの再遷移は無視

    if (previous !== null) {
      recordLeaveRef.current(previous);
    }

    enteredAtRef.current = Date.now();
    hiddenTotalRef.current = 0;
    hiddenSinceRef.current = document.visibilityState === 'hidden' ? Date.now() : null;

    if (!isExcludedPath(path)) {
      const searchKeys = Array.from(new URLSearchParams(location.search).keys());
      const referrer = previous ?? referrerPath();
      enqueue({
        event_type: 'page_view',
        event_name: 'page_view',
        page_path: path,
        page_title: document.title || undefined,
        referrer_path: referrer,
        props: searchKeys.length > 0 ? { search_keys: searchKeys } : undefined,
      });
    }

    previousPathRef.current = path;
  }, [location.pathname, location.search]);

  return null;
};

const referrerPath = (): string | undefined => {
  if (!document.referrer) return undefined;
  try {
    const url = new URL(document.referrer);
    return url.origin === window.location.origin ? url.pathname : url.hostname;
  } catch {
    return undefined;
  }
};

export default BehaviorTrackerRoot;
